import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './feedbackpopup.css';
import { supabase } from '../supabase';
import star from '../assets/star.svg';
import { gsap, prefersReducedMotion } from '../hooks/useGsapAnimations';

const FEEDBACK_DELAY_MS = 30000;
const FEEDBACK_DUE_KEY = 'techne_feedback_due_at';
const FEEDBACK_DONE_KEY = 'techne_feedback_done';

const FeedbackPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const popupRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem(FEEDBACK_DONE_KEY) === 'true') return undefined;

    const currentDueAt = Number(sessionStorage.getItem(FEEDBACK_DUE_KEY));
    const dueAt = currentDueAt || Date.now() + FEEDBACK_DELAY_MS;

    if (!currentDueAt) {
      sessionStorage.setItem(FEEDBACK_DUE_KEY, String(dueAt));
    }

    const remainingTime = Math.max(dueAt - Date.now(), 0);
    const timerId = window.setTimeout(() => setIsVisible(true), remainingTime);

    return () => window.clearTimeout(timerId);
  }, []);

  useLayoutEffect(() => {
    if (!isVisible || !popupRef.current || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      gsap.from('.feedback_popup_overlay', {
        autoAlpha: 0,
        duration: 0.18,
        ease: 'power2.out',
      });

      gsap.from('.feedback_popup', {
        autoAlpha: 0,
        y: 34,
        scale: 0.92,
        duration: 0.48,
        ease: 'back.out(1.55)',
      });
    }, popupRef);

    return () => ctx.revert();
  }, [isVisible]);

  const closePopup = () => {
    sessionStorage.setItem(FEEDBACK_DONE_KEY, 'true');
    setIsVisible(false);
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    setStatus('');
    setIsSubmitting(true);

    const { error } = await supabase
      .from('feedback')
      .insert([
        {
          full_name: fullName,
          email,
          rating,
          message,
        },
      ]);

    setIsSubmitting(false);

    if (error) {
      setStatus(error.message);
      return;
    }

    sessionStorage.setItem(FEEDBACK_DONE_KEY, 'true');
    setStatus('Thank you for your feedback.');
    window.setTimeout(() => setIsVisible(false), 700);
  };

  if (!isVisible) return null;

  return (
    <div className="feedback_popup_overlay" ref={popupRef} role="dialog" aria-modal="true" aria-labelledby="feedback-title">
      <form className="feedback_popup" onSubmit={submitFeedback}>
        <div className="feedback_popup_header">
          <div>
            <p>QUICK FEEDBACK</p>
            <h2 id="feedback-title">How was Techne?</h2>
          </div>
          <button type="button" onClick={closePopup} aria-label="Close feedback">
            x
          </button>
        </div>

        <div className="feedback_rating" role="group" aria-label="Rating">
          {[1, 2, 3, 4, 5].map((ratingValue) => (
            <button
              className={ratingValue <= rating ? 'active_feedback_star' : ''}
              key={ratingValue}
              type="button"
              onClick={() => setRating(ratingValue)}
              aria-pressed={ratingValue === rating}
              aria-label={`${ratingValue} star rating`}
            >
              <img src={star} alt="" />
            </button>
          ))}
        </div>

        <label className="sr_only" htmlFor="feedback-full-name">Full name</label>
        <input
          id="feedback-full-name"
          type="text"
          placeholder="Full name"
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
          autoComplete="name"
          required
        />

        <label className="sr_only" htmlFor="feedback-email">Email</label>
        <input
          id="feedback-email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
        />

        <label className="sr_only" htmlFor="feedback-message">Feedback message</label>
        <textarea
          id="feedback-message"
          placeholder="Write your feedback"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          required
        />

        {status && <p className="feedback_status" role="status">{status}</p>}

        <div className="feedback_popup_actions">
          <button type="button" onClick={closePopup}>
            Later
          </button>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Feedback'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FeedbackPopup;
